import { api, type Execution, type ExecutionItem, type ReleaseWorkPack, type SignItem } from '../api';
import { callout, field, hoursVal, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { hoursInputAttrs, hoursValue } from '../hours';
import { issuePartsDialog, returnPartDialog } from '../dialogs/issue-parts';
import { errorCard, esc, fmtDate, fmtDecimalHours, fmtHours, html, loading, pill, tile, toast, type State } from '../ui';
import { bindCrsPdf } from './aircraft';

/**
 * Working a pack.
 *
 * Two acts on one screen because they happen in one visit, but they are never
 * blurred together: each item is signed off individually — that is the
 * maintenance record, and it is what resets the interval — and the pack is
 * released as a whole with a CRS at the end.
 *
 * The release panel stays visibly locked until every item is resolved, so the
 * order of operations is legible from the screen rather than learned from an
 * error message.
 */

const ITEM_STATE: Record<string, State> = {
  OPEN: 'NA',
  DONE: 'OK',
  DEFERRED: 'DUE_SOON',
  NOT_REQUIRED: 'NA',
};

const ITEM_LABEL: Record<string, string> = {
  OPEN: 'Open',
  DONE: 'Done',
  DEFERRED: 'Deferred',
  NOT_REQUIRED: 'Not required',
};

const WO_STATE: Record<string, State> = {
  DRAFT: 'NA', PLANNED: 'NA', IN_WORK: 'DUE_SOON',
  AWAITING_PARTS: 'DUE_SOON', COMPLETED: 'OK', CANCELLED: 'NA',
};

function itemRow(i: ExecutionItem, released: boolean): string {
  const signable = i.status === 'OPEN' && !released;
  return html`
    <div class="row" style="border-left-color:var(--${
      i.status === 'DONE' ? 'ok' : i.defectSeverity === 'GROUNDING' ? 'exceed' : i.status === 'OPEN' ? 'line-2' : 'caution'
    })">
      <div class="bd">
        <div class="t">${esc(i.description)}</div>
        <div class="m">
          ${esc([
            i.kind === 'TASK' ? 'Scheduled task' : i.kind === 'DEFECT' ? 'Defect' : i.kind === 'ADHOC' ? 'Additional work' : i.kind,
            i.taskCode,
            i.defectReference,
            i.manHours !== null ? `${fmtDecimalHours(i.manHours)} h` : null,
            i.performedAt ? `signed ${fmtDate(i.performedAt)}` : null,
          ].filter(Boolean).join(' · '))}
        </div>
        ${i.needsJustification && signable
          ? html`<div class="m" style="color:var(--caution)">Outside its tolerance band — signing this asks for a justification</div>`
          : ''}
      </div>
      ${pill(ITEM_STATE[i.status] ?? 'NA', ITEM_LABEL[i.status] ?? i.status)}
      ${signable
        ? html`<button class="btn sm" data-remove-item="${esc(i.id)}" title="Take this out of the pack">Remove</button>
               <button class="btn sm" data-sign="${esc(i.id)}">Sign off</button>`
        : ''}
    </div>
  `;
}

function view(e: Execution): string {
  const released = Boolean(e.crs);
  // A cancelled pack is a record of something that was planned and abandoned.
  // Nothing more happens to it, so it is treated exactly like a released one
  // here rather than offering buttons the server will refuse.
  const closed = released || e.status === 'CANCELLED';
  const done = e.items.filter((i) => i.status === 'DONE').length;

  return html`
    <div class="page-h">
      <div>
        <h1>${esc(e.reference)}</h1>
        <p class="dim">
          ${esc(e.title)} · <a href="/fleet/${esc(e.aircraftId)}">${esc(e.registration)}</a> ${esc(e.typeModel)}
        </p>
      </div>
      <div style="display:flex;gap:8px;align-items:center">
        ${pill(WO_STATE[e.status] ?? 'NA', e.status.replace(/_/g, ' ').toLowerCase())}
        <a class="btn" href="/planning">Back to planning</a>
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Items signed', value: `${done} / ${e.items.length}`, sub: e.openItems ? `${e.openItems} still open` : 'all resolved', tone: e.openItems ? 'caution' : 'ok' })}
      ${tile({ label: 'Labour', value: e.actualManHours ?? e.estimatedManHours ?? 0, suffix: ' h', sub: e.actualManHours !== null ? 'actual' : 'estimated' })}
      ${tile({ label: 'Airframe', value: fmtHours(e.tsnHours), suffix: ' h', sub: 'at sign-off' })}
      ${tile({
        label: 'Release',
        value: released ? e.crs!.reference : '—',
        sub: released ? `${fmtDate(e.crs!.issuedAt)} · ${e.crs!.issuerName}` : 'not yet issued',
        tone: released ? 'ok' : 'acc',
      })}
    </div>

    <div class="card">
      <div class="card-h">
        <div>
          <h2>Work items</h2>
          <p class="tiny dim">
            Signing an item records that the work was accomplished — that is what resets the interval
          </p>
        </div>
        ${closed
          ? ''
          : html`<div style="display:flex;gap:6px">
              <button class="btn sm" data-add-items>Add work</button>
              <button class="btn sm" data-cancel-pack>Cancel pack</button>
            </div>`}
      </div>
      <div class="rows">
        ${e.items.length
          ? e.items.map((i) => itemRow(i, closed)).join('')
          : html`<div class="row"><div class="bd"><div class="m">
              Nothing in this pack yet. Add the work it is for — a pack with no items cannot be released,
              because a certificate of release certifies work that was carried out.
            </div></div></div>`}
      </div>
    </div>

    ${partsCard(e, closed)}

    <div class="card">
      <div class="card-h">
        <div>
          <h2>Release to service</h2>
          <p class="tiny dim">
            ${released
              ? `Issued ${esc(fmtDate(e.crs!.issuedAt))} under ${esc(e.crs!.basis.replace(/_/g, ' '))}`
              : 'Covers the pack as a whole — this is what lets the aircraft fly again'}
          </p>
        </div>
        ${released
          ? html`<div style="display:flex;gap:6px;align-items:center;flex:none">
              ${pill('OK', 'Released')}
              <button class="btn sm" data-crs-pdf="${esc(e.crs!.id)}" data-crs-ref="${esc(e.crs!.reference)}">
                Certificate
              </button>
            </div>`
          : e.status === 'CANCELLED'
            ? pill('NA', 'Cancelled')
            : html`<div style="display:flex;gap:6px;align-items:center;flex:none">
                ${/* The estimate belongs next to Issue CRS because this is the
                      screen somebody is on when the owner rings to ask what it
                      will come to — and it must stop being offered once the
                      work is released, where the invoice is the document. */ ''}
                <button class="btn sm" data-estimate="${esc(e.id)}" data-ref="${esc(e.reference)}">
                  Estimate
                </button>
                <button class="btn pri" data-release>Issue CRS</button>
              </div>`}
      </div>
      <div class="card-b">
        ${released
          ? callout('ok', '✓', html`
              <b>${esc(e.crs!.reference)}</b> issued by ${esc(e.crs!.issuerName)} on
              ${esc(fmtDate(e.crs!.issuedAt))}. The pack is closed and its items can no longer be changed.
            `)
          : e.status === 'CANCELLED'
            ? callout('info', 'i', html`
                This pack was cancelled and will not be worked. It stays on the record because it was
                planned, but nothing is released against it.
              `)
          : e.canRelease
            ? callout('info', 'i', html`
                Every item is resolved. The release basis has to be one ${esc(e.regime.replace(/_/g, '-'))}
                recognises, and the signatory has to hold the matching authorisation.
              `)
            : e.items.length === 0
              ? callout('caution', '!', html`
                  This pack has nothing in it. A certificate of release certifies that work was carried
                  out, so there has to be work — add the items this pack is for before releasing it.
                `)
              : callout('caution', '!', html`
                  ${e.openItems} item(s) still open. Sign or defer every item before releasing the
                  aircraft.
                `)}
      </div>
    </div>
  `;
}

/**
 * What went onto the aircraft.
 *
 * Each line keeps the batch and the release reference it was issued under,
 * copied at the moment of issue rather than read back from the store — the
 * shelf figure moves, and the pack has to go on saying what actually went on
 * this aeroplane. It is also the far end of a batch recall.
 */
function partsCard(e: Execution, closed: boolean): string {
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Parts fitted</h2>
          <p class="tiny dim">
            Issued from the store, with the batch and release they came in on
          </p>
        </div>
        ${closed ? '' : '<button class="btn sm" data-issue-parts>Issue from the store</button>'}
      </div>
      <div class="rows">
        ${e.parts.length
          ? e.parts
              .map(
                (p) => html`
                  <div class="row">
                    <div class="bd">
                      <div class="t">
                        <span style="font-family:var(--mono)">${esc(p.partNumber)}</span>
                        ${esc(p.description)}
                        ${p.stockLotId ? '' : '<span class="sub2">not from the store</span>'}
                      </div>
                      <div class="m tiny dim">
                        ${esc(String(p.quantity))} ${esc(p.unit)}
                        ${p.batchLot ? ` · batch ${esc(p.batchLot)}` : ''}
                        ${p.serialNumber ? ` · S/N ${esc(p.serialNumber)}` : ''}
                        ${p.releaseRef ? ` · ${esc(p.releaseKind?.replace(/_/g, ' ') ?? '')} ${esc(p.releaseRef)}` : ''}
                        ${p.salePrice === null
                          ? '<span class="sub2" style="color:var(--caution)">no sale price — will not be billed</span>'
                          : ` · ${(p.quantity * p.salePrice).toFixed(2)} to the customer`}
                      </div>
                    </div>
                    ${closed || !p.stockLotId
                      ? ''
                      : html`<button class="btn sm" data-return-part="${esc(p.id)}">Return</button>`}
                  </div>
                `,
              )
              .join('')
          : html`<div class="row"><div class="bd"><div class="m">
              Nothing issued to this pack. Parts fitted from the store are recorded here, which is
              what makes a manufacturer’s batch recall a question with an answer.
            </div></div></div>`}
      </div>
    </div>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

const RELEASES = [
  ['EASA_FORM_1', 'EASA Form 1'],
  ['FAA_8130_3', 'FAA 8130-3'],
  ['DUAL_RELEASE', 'Dual release (Form 1 / 8130-3)'],
  ['CoC', 'Certificate of conformity'],
  ['SHELF_LIFE_CERT', 'Shelf-life certificate'],
] as const;

function signOffDialog(e: Execution, item: ExecutionItem, reload: () => void): void {
  const staff = e.certifyingStaff;
  let outcome = 'DONE';
  // Component lines only. No default: the two outcomes do different things to
  // the part, and picking one for somebody is picking what its record says.
  let did = '';

  openDialog<SignItem>({
    title: 'Sign off work item',
    sub: item.description,
    primary: 'Sign',
    body: () => html`
      <div class="frow3">
        ${field('Outcome', select('outcome', [
          opt('DONE', 'Carried out', outcome === 'DONE'),
          opt('DEFERRED', 'Deferred to a later visit', outcome === 'DEFERRED'),
          opt('NOT_REQUIRED', 'Not required', outcome === 'NOT_REQUIRED'),
        ].join(''), 'data-outcome'))}
        ${field('Carried out by', select('performedByPersonId',
          staff.map((s) => opt(s.id, `${s.displayName}${s.licence ? ` — ${s.licence}` : ''}`)).join('')))}
        ${field('Date', input('performedAt', todayISO(), 'type="date"'))}
      </div>
      ${field('Man-hours', input('manHours', item.manHours ?? '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
      ${item.directiveId && outcome === 'DONE'
        ? html`
            <div class="frow">
              ${field('How compliance was achieved', select('directiveMethod', [
                opt('INSPECTION', 'Inspection'),
                opt('MODIFICATION', 'Modification'),
                opt('REPLACEMENT', 'Replacement'),
                opt('TERMINATING_ACTION', 'Terminating action'),
                opt('RECORDS_CHECK', 'Records check'),
              ].join('')))}
              ${field('Ends the recurrence', select('isTerminating', [
                opt('no', 'No — the directive repeats'),
                opt('yes', 'Yes — terminating action'),
              ].join('')), 'Only where the directive offers one')}
            </div>
          `
        : ''}
      ${item.component && outcome === 'DONE'
        ? html`
            <div class="fset">
              <div class="lg">What was done to the part</div>
              ${!item.component.stillFitted
                ? callout('caution', '!', html`
                    <b>${esc(item.component.description)} is no longer fitted to
                    ${esc(e.registration)}.</b> This line cannot clear a life that has already come
                    off. Take the line off the pack, or fit the part again first.
                  `)
                : html`
                    <p class="tiny dim" style="margin:0 0 10px">
                      ${esc(item.component.partNumber)}${item.component.serialNumber ? ` s/n ${esc(item.component.serialNumber)}` : ''}
                      at ${esc(item.component.position ?? '—')} ·
                      TSN ${fmtHours(item.component.tsnHours)} h · TSO ${fmtHours(item.component.tsoHours)} h
                    </p>
                    ${field('What happened', select('did', [
                      opt('', 'Choose…', did === ''),
                      opt('OVERHAULED', 'Overhauled — the same part goes back on', did === 'OVERHAULED'),
                      opt('REPLACED', 'Replaced — a different part goes on', did === 'REPLACED'),
                    ].join(''), 'data-did'))}

                    ${did === 'OVERHAULED'
                      ? callout('info', 'i', html`
                          Time since overhaul goes to zero and the overhaul date is recorded. <b>Time
                          since new keeps running</b> — an overhaul does not make a part new.
                        `)
                      : ''}

                    ${did === 'REPLACED'
                      ? html`
                          <div class="frow3">
                            ${field('Part number', input('rPartNumber', item.component.partNumber, 'style="font-family:var(--mono)" maxlength="60"'))}
                            ${field('Serial number', input('rSerialNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
                            ${field('Where the old one went', select('removedTo', [
                              opt('UNSERVICEABLE', 'Unserviceable'),
                              opt('IN_REPAIR', 'Away for repair'),
                              opt('SERVICEABLE', 'Serviceable — back to the shelf'),
                              opt('SCRAPPED', 'Scrapped'),
                            ].join('')))}
                          </div>
                          <div class="frow3">
                            ${field('Life', select('rLifeKind', [
                              opt('ON_CONDITION', 'On condition', item.component.lifeKind === 'ON_CONDITION'),
                              opt('HARD_TIME', 'Hard time', item.component.lifeKind === 'HARD_TIME'),
                              opt('LIFE_LIMITED', 'Life limited', item.component.lifeKind === 'LIFE_LIMITED'),
                              opt('SHELF_LIFE', 'Shelf life', item.component.lifeKind === 'SHELF_LIFE'),
                            ].join('')))}
                            ${field('Hours limit', input('rLimitHours', item.component.limitHours ?? '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
                            ${field('Months limit', input('rLimitMonths', item.component.limitMonths ?? '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
                          </div>
                          <div class="frow3">
                            ${field('Hours since new', input('rTsnHours', hoursValue(0), hoursInputAttrs()))}
                            ${field('Made on', input('rManufacturedAt', '', 'type="date"'))}
                            ${field('Last overhauled', input('rLastOverhaulAt', '', 'type="date"'), 'One of these is required for a months limit')}
                          </div>
                        `
                      : ''}

                    ${did
                      ? html`
                          <div class="lg" style="margin-top:12px">The release it goes back on</div>
                          <div class="frow3">
                            ${field('Document', select('releaseKind', RELEASES.map(([v, l]) => opt(v, l)).join('')))}
                            ${field('Form number', input('releaseFormNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
                            ${field('Issued', input('releaseIssuedAt', todayISO(), 'type="date"'))}
                          </div>
                          <div class="frow">
                            ${field('Issuing organisation', input('releaseIssuingOrg', e.performingOrg ?? '', 'maxlength="160"'))}
                            ${field('Work scope', input('releaseWorkScope', did === 'OVERHAULED' ? 'Overhauled' : '', 'maxlength="400"'))}
                          </div>
                          ${callout('info', 'i', html`
                            ML.A.501 permits a component to be installed only when it is released on the
                            appropriate document, and a part going back on after an overhaul is an
                            installation. That is why there is no outcome here without one.
                          `)}
                        `
                      : ''}
                  `}
            </div>
          `
        : ''}
      ${item.needsJustification && outcome === 'DONE'
        ? html`
            ${field('Why it was signed outside the band', input('toleranceJustification', '', 'placeholder="e.g. aircraft unserviceable awaiting parts; ferry flight authorised" maxlength="400"'),
              'Recorded against the task, not just this pack')}
            ${callout('caution', '!', html`
              This task is past its tolerance. The next due is still measured from the ORIGINAL due date, so
              using tolerance does not shift the schedule.
            `)}
          `
        : ''}
      ${field('Notes', input('notes', '', 'maxlength="2000"'))}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-outcome]')?.addEventListener('change', (ev) => {
        outcome = (ev.target as HTMLSelectElement).value;
        refresh();
      });
      root.querySelector<HTMLSelectElement>('[data-did]')?.addEventListener('change', (ev) => {
        did = (ev.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => {
      const person = val(root, 'performedByPersonId');
      if (!person) return { error: 'Say who carried the work out — the signature goes on the maintenance record.' };
      const justification = val(root, 'toleranceJustification');
      const done = val(root, 'outcome') === 'DONE';
      if (item.needsJustification && done && !justification) {
        return { error: 'This task is outside its tolerance band. Record why before signing.' };
      }

      // Everything a component line needs, checked here so the refusal names
      // the field rather than arriving as a server error over a closed dialog.
      let component: SignItem['component'];
      if (item.component && done) {
        if (!item.component.stillFitted) {
          return { error: 'That part is no longer fitted to this aircraft. Take the line off the pack, or fit it again first.' };
        }
        const chose = val(root, 'did');
        if (!chose) {
          return { error: 'Say what happened to the part — overhauled and refitted, or replaced. Its hours depend on which.' };
        }
        if (!val(root, 'releaseIssuingOrg')) {
          return { error: 'A component goes back on a release document. Say who issued it.' };
        }
        const replaced = chose === 'REPLACED';
        if (replaced && !val(root, 'rPartNumber')) {
          return { error: 'A replacement needs at least its part number.' };
        }
        const limitMonths = replaced ? numVal(root, 'rLimitMonths') : undefined;
        if (replaced && limitMonths && !val(root, 'rManufacturedAt') && !val(root, 'rLastOverhaulAt')) {
          return { error: 'A limit in months has to be counted from somewhere. Record when it was made, or when it was last overhauled.' };
        }
        const lifeKind = replaced ? val(root, 'rLifeKind') : undefined;
        if (
          replaced && (lifeKind === 'HARD_TIME' || lifeKind === 'LIFE_LIMITED') &&
          !numVal(root, 'rLimitHours') && !limitMonths
        ) {
          return { error: 'A hard-time or life-limited component needs a limit — hours or months.' };
        }

        component = {
          did: chose as 'OVERHAULED' | 'REPLACED',
          releaseKind: val(root, 'releaseKind'),
          releaseIssuingOrg: val(root, 'releaseIssuingOrg'),
          releaseIssuedAt: val(root, 'releaseIssuedAt'),
          releaseFormNumber: val(root, 'releaseFormNumber') || undefined,
          releaseWorkScope: val(root, 'releaseWorkScope') || undefined,
          removedTo: replaced ? val(root, 'removedTo') || undefined : undefined,
          replacement: replaced
            ? {
                partNumber: val(root, 'rPartNumber'),
                serialNumber: val(root, 'rSerialNumber') || undefined,
                lifeKind: lifeKind || undefined,
                limitHours: numVal(root, 'rLimitHours'),
                limitMonths,
                tsnHours: hoursVal(root, 'rTsnHours'),
                manufacturedAt: val(root, 'rManufacturedAt') || undefined,
                lastOverhaulAt: val(root, 'rLastOverhaulAt') || undefined,
              }
            : undefined,
        };
      }

      return {
        outcome: val(root, 'outcome') as 'DONE' | 'DEFERRED' | 'NOT_REQUIRED',
        performedByPersonId: person,
        performedAt: val(root, 'performedAt') || undefined,
        manHours: numVal(root, 'manHours'),
        toleranceJustification: justification || undefined,
        directiveMethod: val(root, 'directiveMethod') || undefined,
        isTerminating: val(root, 'isTerminating') === 'yes' || undefined,
        notes: val(root, 'notes') || undefined,
        component,
      };
    },
    submit: (payload) => api.planning.signItem(e.id, item.id, payload),
    done: (r) =>
      r.componentRecorded
        ? `${r.componentRecorded} — signed by ${r.signedBy}`
        : `${item.description.slice(0, 40)} — ${r.outcome.toLowerCase()} by ${r.signedBy}`,
    onDone: reload,
  });
}

/**
 * Adding work to a pack already in progress.
 *
 * Plans change: parts arrive, an AD lands mid-visit, something is found with
 * the panels off. The candidates are the same ones the pack was built from —
 * this aircraft's own due tasks and open defects — so nothing can be scheduled
 * here that could not have been scheduled at the start.
 */
function addItemsDialog(e: Execution, reload: () => void): void {
  const ticked = new Set<string>();
  let candidates: Array<{ kind: string; refId: string; title: string; detail: string | null }> = [];
  let loaded = false;

  const key = (c: { kind: string; refId: string }) => `${c.kind}:${c.refId}`;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: `Add work to ${e.reference}`,
    sub: `${e.registration} — already in the pack: ${e.items.length} item(s)`,
    primary: 'Add to the pack',

    body: () => {
      if (!loaded) return html`<p class="muted tiny">Reading what is due…</p>`;
      if (!candidates.length) {
        return callout('info', 'i', html`
          Nothing else is due or outstanding on ${esc(e.registration)} that is not already in this pack.
        `);
      }
      return html`
        <div class="rows" style="max-height:420px;overflow:auto">
          ${candidates
            .map(
              (c) => html`
                <label class="row" style="cursor:pointer">
                  <input type="checkbox" data-pick="${esc(key(c))}" ${ticked.has(key(c)) ? 'checked' : ''}
                         style="margin-right:10px">
                  <div class="bd">
                    <div class="t">${esc(c.title)}</div>
                    <div class="m">${esc(c.detail ?? (c.kind === 'DEFECT' ? 'Defect' : 'Scheduled task'))}</div>
                  </div>
                </label>
              `,
            )
            .join('')}
        </div>
      `;
    },

    onRender: (root, refresh) => {
      if (!loaded) {
        void api.planning
          .workPackContext(e.aircraftId)
          .then((ctx) => {
            // Anything already in the pack is not offered again — the server
            // refuses a duplicate, and offering one invites the error.
            const inPack = new Set(
              e.items.map((i) => `${i.kind}:${i.taskId ?? i.defectId ?? ''}`),
            );
            candidates = (ctx.candidates ?? []).filter((c) => !inPack.has(`${c.kind}:${c.refId}`));
            loaded = true;
            refresh();
          })
          .catch(() => {
            loaded = true;
            refresh();
          });
        return;
      }
      root.querySelectorAll<HTMLInputElement>('[data-pick]').forEach((box) =>
        box.addEventListener('change', () => {
          if (box.checked) ticked.add(box.dataset.pick!);
          else ticked.delete(box.dataset.pick!);
        }),
      );
    },

    collect: () => {
      if (!ticked.size) return { error: 'Pick at least one item to add.' };
      return {
        items: [...ticked].map((k) => {
          const [kind, ...rest] = k.split(':');
          return { kind, refId: rest.join(':') };
        }),
      };
    },

    submit: (payload) => api.planning.addItems(e.id, payload.items as never),
    done: (r) => `${r.added} item(s) added to ${r.reference}`,
    onDone: reload,
  });
}

/**
 * Cancelling a pack that will not be worked.
 *
 * The pack stays on the record — planned and abandoned is part of what
 * happened — but it stops counting as open work, which is what blocks ending
 * management of the aircraft and what the capacity view measures.
 */
function cancelPackDialog(e: Execution, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: `Cancel ${e.reference}`,
    sub: `${e.registration} — ${e.items.length} item(s), ${e.openItems} still open`,
    primary: 'Cancel the pack',
    body: () => html`
      ${callout('caution', '!', html`
        The pack is kept, marked cancelled, so the record still shows it was planned. Any defect it was
        going to rectify goes back to waiting for a visit.
        <br><br>
        Work that has already been signed cannot be cancelled — that has to be released instead, on a
        certificate for what was actually done.
      `)}
      ${field('Why', input('reason', '', 'placeholder="Slot released; the owner deferred the visit" maxlength="400"'))}
    `,
    collect: (root) => {
      const reason = val(root, 'reason').trim();
      if (!reason) return { error: 'Say why the pack is being cancelled — it stays on the record either way.' };
      return { reason };
    },
    submit: (payload) => api.planning.cancelPack(e.id, payload.reason as string),
    done: (r) => `${r.reference} cancelled`,
    onDone: reload,
  });
}

function releaseDialog(e: Execution, reload: () => void): void {
  let basis = e.crsBases[0]?.basis ?? '';

  // Only staff holding the authorisation this basis needs may be offered.
  const eligible = () => {
    const need = e.crsBases.find((b) => b.basis === basis)?.authorisation;
    return e.certifyingStaff.filter((s) => !need || s.holds.includes(need));
  };

  openDialog<ReleaseWorkPack>({
    wide: true,
    title: 'Certificate of release to service',
    sub: `${e.reference} — ${e.registration} · ${e.items.filter((i) => i.status === 'DONE').length} item(s) carried out`,
    primary: 'Issue CRS',
    body: () => {
      const staff = eligible();
      const deferred = e.items.filter((i) => i.status === 'DEFERRED');
      return html`
        <div class="frow">
          ${field('Release basis', select('basis', e.crsBases.map((b) => opt(b.basis, b.label, b.basis === basis)).join(''), 'data-basis'))}
          ${field(
            'Signed by',
            staff.length
              ? select('issuedByPersonId', staff.map((s) => opt(s.id, `${s.displayName}${s.licence ? ` — ${s.licence}` : ''}`)).join(''))
              : select('issuedByPersonId', ''),
            staff.length ? 'Only staff holding the authorisation this basis needs' : undefined,
          )}
        </div>
        ${staff.length
          ? ''
          : callout('exceed', '✕', html`
              Nobody on the roster holds the authorisation this basis requires. Choose another basis, or record
              the authorisation against the person first.
            `)}
        ${field('Approval reference', input('issuerApproval', e.performingApproval ?? '', 'placeholder="RO.145.0031" style="font-family:var(--mono)" maxlength="40"'))}
        ${field('Scope — what is being released', input('scopeText', '', 'placeholder="Leave blank to generate from the signed items" maxlength="4000"'))}
        ${field('Limitations', input('limitations', '', `placeholder="${deferred.length ? 'Deferred items are listed automatically' : 'none'}" maxlength="4000"`))}
        ${basis === 'ML_A_801_B3_PILOT_OWNER'
          ? callout('caution', '!', html`
              Pilot-owner maintenance under ML.A.801(b)(3) reaches only the ML Appendix II task list. Anything
              beyond it will be refused.
            `)
          : ''}
        ${deferred.length
          ? callout('info', 'i', html`
              ${deferred.length} item(s) are deferred and will be carried forward on the certificate as a
              limitation.
            `)
          : ''}
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-basis]')?.addEventListener('change', (ev) => {
        basis = (ev.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => {
      const person = val(root, 'issuedByPersonId');
      if (!person) return { error: 'Nobody available holds the authorisation this release basis needs.' };
      return {
        basis: val(root, 'basis'),
        issuedByPersonId: person,
        issuerApproval: val(root, 'issuerApproval') || undefined,
        scopeText: val(root, 'scopeText') || undefined,
        limitations: val(root, 'limitations') || undefined,
      };
    },
    submit: (payload) => api.planning.release(e.id, payload),
    done: (r) => {
      const parts = (r.partCharges ?? []).reduce((n: number, c: { netAmount: number }) => n + c.netAmount, 0);
      const billed = [
        r.chargeable ? `${r.chargeable.netAmount.toFixed(2)} labour` : null,
        parts ? `${parts.toFixed(2)} parts` : null,
      ].filter(Boolean).join(' + ');
      return `${r.reference} issued — ${r.registration} released`
        + (r.aircraftStatus === 'GROUNDED' ? ', still grounded' : '')
        + (billed ? ` · ${billed} raised` : '');
    },
    onDone: (r) => {
      // The release succeeded; the billing did not. Said separately and left on
      // screen longer, because it is the one thing here nobody will notice by
      // looking at the aircraft.
      if (r.notBilled) toast(r.notBilled, 'error');
      reload();
    },
  });
}

// ── entry point ─────────────────────────────────────────────────────────────

export async function renderWorkPack(host: HTMLElement, id: string): Promise<void> {
  host.innerHTML = loading('Loading work pack');
  try {
    const e = await api.planning.execution(id);
    host.innerHTML = view(e);

    const reload = () => void renderWorkPack(host, id);

    bindCrsPdf(host);
    host.querySelector<HTMLElement>('[data-add-items]')?.addEventListener('click', () =>
      addItemsDialog(e, reload),
    );
    host.querySelector<HTMLElement>('[data-cancel-pack]')?.addEventListener('click', () =>
      cancelPackDialog(e, reload),
    );
    host.querySelector<HTMLElement>('[data-issue-parts]')?.addEventListener('click', () =>
      issuePartsDialog(e, reload),
    );
    host.querySelectorAll<HTMLElement>('[data-return-part]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const part = e.parts.find((p) => p.id === btn.dataset.returnPart);
        if (part) returnPartDialog(e, part, reload);
      }),
    );

    host.querySelectorAll<HTMLElement>('[data-remove-item]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const item = e.items.find((i) => i.id === btn.dataset.removeItem);
        if (!item) return;
        const label = btn.textContent;
        btn.textContent = '…';
        try {
          const r = await api.planning.removeItem(e.id, item.id);
          toast(`Removed from ${r.reference}`);
          reload();
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not remove that item.', 'error');
          btn.textContent = label;
        }
      }),
    );

    host.querySelectorAll<HTMLElement>('[data-sign]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const item = e.items.find((i) => i.id === btn.dataset.sign);
        if (item) signOffDialog(e, item, reload);
      }),
    );

    // Deliberately not disabled. A disabled button swallows the click, so
    // somebody who cannot release learns nothing at all from pressing it —
    // which is exactly what this looked like from the outside.
    host.querySelector<HTMLButtonElement>('[data-release]')?.addEventListener('click', () => {
      if (e.items.length === 0) {
        toast('This pack has no items. A release certifies work that was carried out — add the work first.', 'error');
        return;
      }
      if (e.openItems > 0) {
        toast(`${e.openItems} item(s) still open. Sign or defer every one before releasing the aircraft.`, 'error');
        return;
      }
      if (!e.canRelease) {
        toast('This pack cannot be released — it may already carry a certificate.', 'error');
        return;
      }
      releaseDialog(e, reload);
    });
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load that work pack.');
  }
}
